import { useToast } from '@contexts/Toast';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { errorMessage, ProductType, WeightType } from '@helpers/Constants';
import {
  AddonsList,
  EditProductModalProps,
  MasterPackage,
  OrderPackage,
} from '@helpers/Models';
import { useState } from 'react';

export const EditProductModal: React.FC<EditProductModalProps> = ({
  product,
  onClose,
  onSave,
  allAddons,
  allPackages,
  productTypeInfo,
}) => {
  const { showToastMessage } = useToast();

  const [fatPercentage, setFatPercentage] = useState(
    String(product.fatPercentage ?? ''),
  );
  const [quantity, setQuantity] = useState(String(product.quantity ?? ''));
  // const [instruction, setInstruction] = useState(product.instruction ?? '');
  const [notes, setNotes] = useState(product.notes ?? '');
  const [selectedAddons, setSelectedAddons] = useState<AddonsList[]>(
    product.addons ?? [],
  );
  const [selectedPackages, setSelectedPackages] = useState<OrderPackage[]>(
    product.packages ?? [],
  );
  const [percentageError, setPercentageError] = useState<boolean>(false);

  const [openAddons, setOpenAddons] = useState(true);
  const [dropdownOpenIndex, setDropdownOpenIndex] = useState<number | null>(
    null,
  );
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const allowFatContentFlag = Number(
    productTypeInfo.allowFatContentFlag ?? product.allowFatContentFlag ?? 0,
  );
  const allowAddOnFlag = Number(
    productTypeInfo.allowAddOnFlag ?? product.allowAddOnFlag ?? 0,
  );
  const upchargeIngredientsFlag = Number(
    productTypeInfo.upchargeIngredientsFlag ??
    product.upchargeIngredientsFlag ??
    0,
  );

  const isFatEnabled = allowFatContentFlag === 1;
  const isAddonEnabled = allowAddOnFlag === 1;
  const isAddonUpchargeEnabled = isAddonEnabled && upchargeIngredientsFlag === 1;

  const toggleAddon = (addon: AddonsList) => {
    setSelectedAddons((prev) => {
      const exists = prev.some((a) => a.id === addon.id);
      if (exists) {
        return prev.filter((a) => a.id !== addon.id);
      }

      return [
        ...prev,
        {
          ...addon,
          price: isAddonUpchargeEnabled ? addon.price : 0,
        },
      ];
    });
  };

  const handleDeletePackage = (packageId: number) => {
    setSelectedPackages((prev) => prev.filter((p) => p.id !== packageId));
  };

  const handleSelectPackage = (pack: MasterPackage) => {
    setSelectedPackages((prev) => {
      if (prev.some((p) => p.id === pack.id)) return prev;

      const updated = [
        ...prev,
        { ...pack, name: pack.packageType, percentage: '' },
      ];

      if (updated.length === 2) {
        updated[0] = { ...updated[0], percentage: '50' };
        updated[1] = { ...updated[1], percentage: '50' };
      }

      return updated;
    });

    // setSelectedPackages((prev) => [...prev, { ...pack, percentage: '' }]);
    setDropdownOpenIndex(null);
  };

  /**
   * Validates the entered package percentage value on input blur.
   *
   * Displays an error toast and sets validation state when the value
   * is invalid. A valid percentage must be greater than 0 and less than 100.
   *
   * @param value - Percentage value entered by the user.
   */
  const handlePercentageBlur = (value: string): void => {
    const num = Number(value);

    if (isNaN(num) || num <= 0 || num >= 100) {
      setPercentageError(true);
      showToastMessage(errorMessage.ORDER_PACKAGE_PERCENTAGE_INVALID, 'error');
    } else {
      setPercentageError(false);
    }
  };

  /**
   * Updates the percentage value for a selected package.
   *
   * Ensures the entered percentage stays within the range of 0 to 100.
   * When exactly two packages are selected, the remaining package
   * percentage is automatically adjusted so the total equals 100.
   *
   * @param index - Index of the package being updated.
   * @param value - Percentage value entered by the user.
   */
  const handlePercentageChange = (index: number, value: string): void => {
    const num = Math.max(0, Math.min(100, Number(value)));

    setSelectedPackages((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        percentage: value,
      };

      if (updated.length === 2) {
        const otherIndex = index === 0 ? 1 : 0;

        updated[otherIndex] = {
          ...updated[otherIndex],
          percentage: String(100 - num),
        };
      }

      return updated;
    });
  };

  /**
   * Filters and returns packages that are not currently selected.
   *
   * Compares all available packages with the selected packages list
   * and excludes packages whose IDs already exist in the selected list.
   */
  const remainingPackages = allPackages.filter(
    (p) => !selectedPackages.some((sp) => sp.id === p.id),
  );

  /**
   * Validates product form data and saves the product details.
   *
   * Performs the following validations before saving:
   * - Stops save action if package percentage validation has failed.
   * - Validates fat percentage when required by the product.
   * - Ensures quantity meets the minimum allowed value based on product type.
   * - Ensures quantity is provided and greater than zero.
   *
   * If validation fails, corresponding error messages are stored in state.
   * Otherwise, the updated product data is passed to the save handler.
   */
  const handleSave = (): void => {
    const newErrors: { [key: string]: string } = {};
    const trimmedQuantity = quantity.trim();
    const parsedQuantity = Number(trimmedQuantity);

    if (percentageError) {
      return;
    }

    if (isFatEnabled) {
      if (!fatPercentage || fatPercentage === '') {
        newErrors.fatPercentage = errorMessage.FIELD_REQUIRED;
      }
    }

    if (trimmedQuantity === '' || Number.isNaN(parsedQuantity)) {
      newErrors.quantity = errorMessage.FIELD_REQUIRED;
    } else if (parsedQuantity < productTypeInfo.minValue) {
      newErrors.quantity = `Entered value should be at least ${productTypeInfo.minValue}`;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    onSave({
      ...product,
      fatPercentage,
      quantity: parsedQuantity,
      notes,
      addons: isAddonEnabled ? selectedAddons : [],
      packages: selectedPackages as OrderPackage[],
    });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white w-full max-w-2xl max-h-[95vh] overflow-y-auto rounded-lg shadow-lg p-3">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white flex justify-between items-center p-2 border-b">
          <div className="flex items-center gap-2">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="shrink-0"
            >
              <path
                d="M12 0C5.34375 0 0 5.39062 0 12C0 18.6562 5.34375 24 12 24C18.6094 24 24 18.6562 24 12C24 5.39062 18.6094 0 12 0ZM12 6C12.7969 6 13.5 6.70312 13.5 7.5C13.5 8.34375 12.7969 9 12 9C11.1562 9 10.5 8.34375 10.5 7.5C10.5 6.70312 11.1562 6 12 6ZM13.875 18H10.125C9.46875 18 9 17.5312 9 16.875C9 16.2656 9.46875 15.75 10.125 15.75H10.875V12.75H10.5C9.84375 12.75 9.375 12.2812 9.375 11.625C9.375 11.0156 9.84375 10.5 10.5 10.5H12C12.6094 10.5 13.125 11.0156 13.125 11.625V15.75H13.875C14.4844 15.75 15 16.2656 15 16.875C15 17.5312 14.4844 18 13.875 18Z"
                fill="#376BFC"
              />
            </svg>
            <h2 className="text-xl font-medium">Edit Product</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 text-lg leading-none"
          >
            ✕
          </button>
        </div>

        <div className="p-1">
          <div className="mt-2 bg-white p-4 rounded-xl border border-gray-200">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
              {/* Product Info */}
              <div className="p-4 bg-[#F9F3F1] rounded-lg border border-gray-200">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold xl:text-lg text-[#1A1C1E]">
                      {product.name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {productTypeInfo.productType === ProductType.QUANTITY
                        ? `Min Quantity: ${product.quantity}`
                        : `Min Weight: ${product.quantity} ${WeightType.LBS}`}{' '}
                    </p>
                  </div>
                  <div className="font-bold text-[#1A1C1E] ml-2">
                    ${product.price}/{WeightType.LBS}
                  </div>
                </div>
                {isFatEnabled && (
                  <div className="flex items-center gap-4 mt-4">
                    <span className="font-medium">Fat Percentage:</span>
                    <div className="relative">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={fatPercentage}
                        onChange={(e) => {
                          setFatPercentage(e.target.value);
                          if (e.target.value)
                            setErrors((prev) => ({
                              ...prev,
                              fatPercentage: '',
                            }));
                        }}
                        className={`w-full max-w-[160px] bg-white border rounded py-1.5 pl-3 pr-8 font-bold text-lg shadow-sm outline-none focus:ring-1 focus:ring-primary`}
                      />
                      {errors.fatPercentage && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.fatPercentage}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Measurement Type */}
              <div>
                <h4 className="font-medium mb-3 text-lg">Measurement Type</h4>
                <label className="flex items-center gap-2 mb-2">
                  {productTypeInfo.productType == ProductType.QUANTITY
                    ? 'Quantity'
                    : 'Weight'}
                </label>
                <input
                  type="number"
                  max="100"
                  value={quantity}
                  onChange={(e) => {
                    setQuantity(e.target.value);
                    if (e.target.value)
                      setErrors((prev) => ({ ...prev, quantity: '' }));
                  }}
                  placeholder="Quantity"
                  className={`w-full border rounded-md p-2 text-sm mb-2 shadow-sm outline-none focus:ring-1 focus:ring-primary`}
                />
                {errors.quantity && (
                  <p className="text-red-500 text-xs mt-1">{errors.quantity}</p>
                )}
                {/* <div className="border border-green-600 bg-green-50 rounded-md p-3">
                                    <label className="flex items-center gap-2 mb-2">
                                        <input type="radio" name="measure" className="accent-primary" defaultChecked />
                                        Cuts of Meat / Instructions
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Add Cuts of Meat/Instructions"
                                        value={instruction}
                                        onChange={(e) => setInstruction(e.target.value)}
                                        className="w-full border rounded-md p-2 text-sm"
                                    />
                                </div> */}
              </div>
            </div>

            <div className="mt-8">
              {isAddonEnabled && (
                <div className="bg-[#FFFEFA] border border-[#0000001A] rounded-lg">
                  <div
                    className="flex items-center justify-between p-3 rounded-t-lg cursor-pointer"
                    onClick={() => setOpenAddons(!openAddons)}
                  >
                    <h3 className="font-bold text-[#333] text-lg">Add - Ons</h3>
                    <svg
                      className="w-5 h-5 text-gray-500 cursor-pointer"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                  {openAddons && (
                    <div className="border border-t-0 bg-[#FFFEFA] border-[#0000001A] rounded-b-lg px-3 py-2 lg:px-6 lg:py-3">
                      <div className="grid grid-cols-2 gap-x-3 gap-y-0 lg:gap-x-10 lg:gap-y-1.5">
                        {allAddons.map((addon) => (
                          <label
                            key={addon.id}
                            className="flex items-center justify-between cursor-pointer py-1 lg:py-2 min-w-0"
                          >
                            <span
                              className="text-[#333] font-medium text-[11px] md:text-xs lg:text-xs xl:text-base truncate flex-1 min-w-0"
                              title={addon.name}
                            >
                              {addon.name}
                            </span>
                            <div className="flex items-center gap-1 xl:gap-3 shrink-0 ml-1">
                              {isAddonUpchargeEnabled && (
                                <span className="text-[11px] md:text-xs lg:text-xs xl:text-base font-semibold text-gray-600 whitespace-nowrap">
                                  +${addon.price}/Lbs
                                </span>
                              )}
                              <input
                                type="checkbox"
                                className="w-3 h-3 md:w-3.5 md:h-3.5 lg:w-4 lg:h-4 accent-primary cursor-pointer shrink-0"
                                checked={selectedAddons.some(
                                  (a) => a.id === addon.id,
                                )}
                                onChange={() => toggleAddon(addon)}
                              />
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Packaging */}
              <div className="mt-4">
                <h4 className="font-semibold text-[#1C1B1F] mb-3 text-lg">
                  Packaging
                </h4>
                {selectedPackages.map((packaging, index) => (
                  <div
                    key={packaging.id}
                    className="relative flex gap-2 items-center mb-4"
                  >
                    <div className="flex-grow flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-white">
                      <span className="text-gray-700 font-medium">
                        {packaging.packageType}
                      </span>
                      <div className="flex items-center gap-6">
                        <span className="text-gray-500 text-sm">
                          {packaging.packageSize} lbs
                        </span>
                        <div className="flex items-center gap-1 text-gray-600 font-medium">
                          <span>
                            + ${packaging.price?.toFixed(2)} / {packaging.unit}
                          </span>
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            className="text-[#A09CAB] text-sm cursor-pointer"
                            onClick={() =>
                              setDropdownOpenIndex(
                                dropdownOpenIndex === index ? null : index,
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                    {selectedPackages.length > 1 && (
                      <div className="relative w-[80px] ml-2">
                        <input
                          type="number"
                          className={`h-12 w-full pr-5 pl-2 border rounded-md focus:outline-primary text-center ${percentageError
                            ? 'border-red-500'
                            : 'border-gray-300'
                            }`}
                          value={packaging.percentage ?? ''}
                          onChange={(e) =>
                            handlePercentageChange(index, e.target.value)
                          }
                          onBlur={(e) => handlePercentageBlur(e.target.value)}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                          %
                        </span>
                      </div>
                    )}
                    <button
                      type="button"
                      className="p-3 border border-[#D4D4D4] rounded-lg text-[#4D545C]"
                      onClick={() => handleDeletePackage(packaging.id)}
                    >
                      <FontAwesomeIcon icon={faTrashCan} />
                    </button>

                    {/* Package dropdown */}
                    {dropdownOpenIndex === index &&
                      remainingPackages.length > 0 && (
                        <div className="absolute left-0 top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow z-10">
                          <div className="grid grid-cols-3 gap-2 p-3 border-b text-xs font-semibold bg-[#F0F0F0] uppercase">
                            <span>Title</span>
                            <span>Size in lbs</span>
                            <span>Upcharge</span>
                          </div>
                          {remainingPackages.map((pack) => (
                            <div
                              key={pack.id}
                              onClick={() => handleSelectPackage(pack)}
                              className="grid grid-cols-3 gap-2 p-3 hover:bg-gray-100 cursor-pointer text-sm"
                            >
                              <span className="text-gray-700">
                                {pack.packageType}
                              </span>
                              <span className="text-gray-500">
                                {pack.packageSize} lbs
                              </span>
                              <span className="text-gray-600">
                                + ${pack.price?.toFixed(2)} / {pack.unit}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                ))}
                {selectedPackages.length < 2 && (
                  <button
                    type="button"
                    onClick={() => setDropdownOpenIndex(0)}
                    className="w-full py-2.5 border border-primary text-primary font-bold rounded-lg"
                  >
                    Add Packaging
                  </button>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="mt-4">
              <h4 className="font-medium mb-2 text-lg">Notes</h4>
              <textarea
                placeholder="Add notes or description..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full border rounded-md p-3"
              />
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-4 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="text-primary font-bold"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="md:w-[140px] w-[160px] bg-[#0B5D3B] text-white px-6 py-2 rounded-md"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
